import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import { createTheme, PaletteMode, ThemeProvider } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useMutation, UseMutationResult } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import dayjs from "dayjs";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  confirmMutationFn,
  confirmOnError,
  confirmOnSuccess,
  phoneOnError,
  phoneOnSuccess,
  signupMutationFn,
  signupOnError,
  verifyMutationFn,
} from "../../api/sign";
import { KakaoIcon, OdnIcon } from "../../components/Icon/CustomIcons";
import { PasswordTextField } from "../../components/PasswordTextField";
import { ErrorData } from "../../config/interfaces";
import { Card, SignUpContainer } from "../../config/styles";
import TemplateFrame from "../../utils/common/TemplateFrame";
import getSignTheme from "../../utils/common/theme/getSignTheme";
import { inputValidate } from "../../utils/inputValidate";
import { intlTelInputFunc } from "../../utils/intl-tel-input";
import { Timer } from "../../utils/timer";

const sx = {
  width: "100%",
  marginTop: 2,
  marginBottom: 1,
  overflowY: "scroll",
  height: "100%",
};

export default function SignUp() {
  const [mode, setMode] = React.useState<PaletteMode>("light");
  const [showCustomTheme, setShowCustomTheme] = React.useState(true);
  const [showPassword, setShowPassword] = React.useState(false);
  const [dialCode, setDialCode] = React.useState("");
  const [expireTime, setExpireTime] = React.useState("");
  const [timerOn, setTimerOn] = React.useState(false);
  const [signUpReady, setSignUpReady] = React.useState(false);

  const defaultTheme = createTheme({ palette: { mode } });
  const SignUpTheme = createTheme(getSignTheme(mode));

  const [nameCheck, setNameCheck] = React.useState<ErrorData>({
    error: false,
    message: "",
  });

  const [birthCheck, setBirthCheck] = React.useState<ErrorData>({
    error: false,
    message: "",
  });

  const [emailCheck, setEmailCheck] = React.useState<ErrorData>({
    error: false,
    message: "",
  });

  const [passwordCheck, setPasswordCheck] = React.useState<ErrorData>({
    error: false,
    message: "",
  });

  const [passwordConfirmCheck, setPasswordConfirmCheck] =
    React.useState<ErrorData>({
      error: false,
      message: "",
    });

  const [phoneCheck, setPhoneCheck] = React.useState<ErrorData>({
    error: false,
    message: "",
  });

  const [codeCheck, setCodeCheck] = React.useState<ErrorData>({
    error: false,
    message: "",
  });

  const navigate = useNavigate();

  React.useEffect(() => {
    const input = document.querySelector("#phone") as Element;
    intlTelInputFunc({ setDialCode: setDialCode, input: input });
  }, []);

  // This code only runs on the client side, to determine the system color preference
  React.useEffect(() => {
    // Check if there is a preferred mode in localStorage
    const savedMode = localStorage.getItem("themeMode") as PaletteMode | null;
    if (savedMode) {
      setMode(savedMode);
    } else {
      // If no preference is found, it uses system preference
      const systemPrefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)"
      ).matches;
      setMode(systemPrefersDark ? "dark" : "light");
    }
  }, []);

  const toggleColorMode = () => {
    const newMode = mode === "dark" ? "light" : "dark";
    setMode(newMode);
    localStorage.setItem("themeMode", newMode); // Save the selected mode to localStorage
  };

  const toggleCustomTheme = () => {
    setShowCustomTheme((prev) => !prev);
  };

  const signupMutation = useMutation({
    mutationFn: (data: Object) => signupMutationFn(data),
    onError: (error) => signupOnError(error),
    onSuccess: () => navigate(-1),
  });

  const verifyMutation = useMutation({
    mutationFn: (data: Object) => verifyMutationFn(data),
    onError: () => phoneOnError(setEmailCheck, dialCode),
    onSuccess: (data: AxiosResponse<any, any>) =>
      phoneOnSuccess({
        data: data,
        setCheck: setEmailCheck,
        setTimerOn: setTimerOn,
        setState: setExpireTime,
        dialCode: dialCode,
      }),
  });

  const confirmMutation = useMutation({
    mutationFn: (data: Object) => confirmMutationFn(data),
    onError: () => confirmOnError(setCodeCheck, dialCode),
    onSuccess: (data: AxiosResponse<any, any>) =>
      confirmOnSuccess({
        data: data,
        setCheck: setCodeCheck,
        setTimerOn: setTimerOn,
        setState: setSignUpReady,
        dialCode: dialCode,
      }),
  });

  const validateInputs = () => {
    const inputData = [
      {
        type: "name",
        setState: setNameCheck,
      },
      {
        type: "email",
        setState: setEmailCheck,
      },
      {
        type: "password",
        setState: setPasswordCheck,
      },
      {
        type: "password-confirm",
        setState: setPasswordConfirmCheck,
      },
      {
        type: "phone",
        setState: setPhoneCheck,
      },
      {
        type: "code",
        setState: setCodeCheck,
      },
      {
        type: "birth",
        setState: setBirthCheck,
      },
    ];

    inputData.map((res) =>
      inputValidate({
        ...res,
        props: { dialCode: dialCode },
      })
    );
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    let phoneData;
    const data = new FormData(event.currentTarget);
    event.preventDefault();

    const regex = /[^0-9]/g;

    const originalNumber = data.get("phone") as string;
    if (originalNumber.charAt(0) === "0") {
      phoneData = `+${dialCode}${originalNumber
        .substring(1)
        .replace(regex, "")}`;
    } else {
      phoneData = `+${dialCode}${originalNumber.replace(regex, "")}`;
    }

    const userData = {
      name: data.get("name"),
      email: data.get("email"),
      password1: data.get("password"),
      password2: data.get("password-confirm"),
      phone: phoneData,
      birthdate: data.get("birth"),
    };

    signupMutation.mutate(userData);
  };

  return (
    <TemplateFrame
      toggleCustomTheme={toggleCustomTheme}
      showCustomTheme={showCustomTheme}
      mode={mode}
      toggleColorMode={toggleColorMode}>
      <ThemeProvider theme={showCustomTheme ? SignUpTheme : defaultTheme}>
        <CssBaseline enableColorScheme />

        <SignUpContainer direction="column" justifyContent="space-between">
          <Stack sx={{ ...sx }}>
            <Card
              variant="outlined"
              style={{ overflowY: "scroll", width: "95%" }}>
              <OdnIcon mode={mode} />
              <Box
                noValidate
                component="form"
                onSubmit={handleSubmit}
                sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <FormControl>
                  <FormLabel htmlFor="name">
                    {dialCode === "82" ? "이름" : "Name"}
                  </FormLabel>
                  <TextField
                    autoComplete="name"
                    name="name"
                    required
                    fullWidth
                    id="name"
                    placeholder={
                      dialCode === "82"
                        ? "이름을 입력해주세요."
                        : "Please provide your full name."
                    }
                    error={nameCheck.error}
                    helperText={nameCheck.message}
                    color={nameCheck.error ? "error" : "primary"}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel htmlFor="tel">
                    {dialCode === "82"
                      ? "전화번호"
                      : `Phone number (+${dialCode})`}
                  </FormLabel>
                  <TextField
                    required
                    fullWidth
                    name="phone"
                    type="tel"
                    id="phone"
                    // placeholder="01000000000"
                    autoComplete="tel"
                    variant="outlined"
                    error={phoneCheck.error}
                    helperText={phoneCheck.message}
                    color={phoneCheck.error ? "error" : "primary"}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel htmlFor="date">
                    {dialCode === "82" ? "생년월일" : "Date of birth"}
                  </FormLabel>
                  <DatePicker
                    format="YYYY-MM-DD"
                    // showDaysOutsideCurrentMonth
                    shouldDisableDate={(day) => {
                      return dayjs(dayjs(day).format(`YYYY-MM-DD`)).isAfter(
                        new Date()
                      );
                    }}
                    slotProps={{
                      textField: {
                        name: "birth",
                        required: true,
                        fullWidth: true,
                        id: "birth",
                        placeholder: dayjs(new Date()).format(`YYYY-MM-DD`),
                        error: birthCheck.error,
                        helperText: birthCheck.message,
                        color: birthCheck.error ? "error" : "primary",
                      },
                    }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="email">
                    {dialCode === "82" ? "이메일" : "Email"}
                  </FormLabel>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    placeholder="example@email.com"
                    name="email"
                    autoComplete="email"
                    variant="outlined"
                    error={emailCheck.error}
                    helperText={emailCheck.message}
                    color={emailCheck.error ? "error" : "primary"}
                  />
                </FormControl>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    inputValidate({
                      type: "email",
                      setState: setEmailCheck,
                      mutation: verifyMutation as UseMutationResult,
                      props: { dialCode: dialCode },
                    });
                  }}>
                  {dialCode === "82"
                    ? "인증코드 받기"
                    : "Get verification code"}
                </Button>

                <FormControl>
                  <FormLabel htmlFor="code">
                    {dialCode === "82" ? "인증코드" : "Verification code"}
                  </FormLabel>
                  <TextField
                    required
                    fullWidth
                    name="code"
                    placeholder="••••••"
                    type="number"
                    id="code"
                    variant="outlined"
                    error={codeCheck.error}
                    helperText={codeCheck.message}
                    color={codeCheck.error ? "error" : "primary"}
                  />
                </FormControl>

                {timerOn ? (
                  <Timer
                    expireTime={expireTime}
                    setTimerOn={setTimerOn}
                    setState={setCodeCheck}
                    signUpReady={signUpReady}
                  />
                ) : null}

                <Button
                  fullWidth
                  variant="contained"
                  onClick={() =>
                    inputValidate({
                      type: "code",
                      setState: setCodeCheck,
                      mutation: confirmMutation as UseMutationResult,
                      props: { dialCode: dialCode, method: "email" },
                    })
                  }>
                  {dialCode === "82" ? "인증하기" : "Code verify"}
                </Button>

                <FormControl>
                  <FormLabel htmlFor="password">
                    {dialCode === "82" ? "비밀번호" : "Password"}
                  </FormLabel>
                  <PasswordTextField
                    passwordCheck={passwordCheck}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="password">
                    {dialCode === "82" ? "비밀번호 확인" : "Confirm password"}
                  </FormLabel>
                  <PasswordTextField
                    passwordCheck={passwordConfirmCheck}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                    type="password-confirm"
                  />
                </FormControl>

                {signUpReady ? (
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    onClick={validateInputs}>
                    {dialCode === "82" ? "회원가입 하기" : "Sign up"}
                  </Button>
                ) : null}

                <Typography sx={{ textAlign: "center" }}>
                  {dialCode === "82"
                    ? "이미 회원이신가요?"
                    : "Already have account?"}{" "}
                  <span
                  // onClick={() => navigate(-1)}
                  >
                    <Link
                      onClick={() => navigate(-1)}
                      variant="body2"
                      sx={{ alignSelf: "center", cursor: "pointer" }}>
                      {dialCode === "82" ? "로그인 화면으로" : "Back to log in"}
                    </Link>
                  </span>
                </Typography>
              </Box>
              <Divider sx={{ display: "none" }}>
                <Typography sx={{ color: "text.secondary" }}>or</Typography>
              </Divider>
              <Box
                sx={{
                  display: "none", // "flex"
                  flexDirection: "column",
                  gap: 2,
                }}>
                <Button
                  style={{ backgroundColor: "#FFE812", color: "black" }}
                  type="submit"
                  fullWidth
                  // variant="outlined"
                  onClick={() => alert("Sign in with Kakao")}
                  startIcon={<KakaoIcon />}>
                  Sign in with Kakao
                </Button>
              </Box>
            </Card>
          </Stack>
        </SignUpContainer>
      </ThemeProvider>
    </TemplateFrame>
  );
}
